<template>
  <div>
    <base-header class="pb-6" type="">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 d-inline-block mb-0">Pedidos Tienda</h6>
          <nav aria-label="breadcrumb" class="d-none text-black d-md-inline-block ml-md-4">
            <route-breadcrumb/>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div class="row">
        <div class="col">
          <div class="card">
            <!-- Card header -->
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">Producción Diaria - Pedidos Tienda</h3>
                </div>
              </div>
            </div>

            <!-- Filtros -->
            <div class="card-body">
              <div class="row">
                <div class="col-md-3">
                  <base-input label="Fecha Desde">
                    <input
                      type="date"
                      class="form-control"
                      v-model="filters.date_from"
                      @change="loadOrders"
                    />
                  </base-input>
                </div>
                <div class="col-md-3">
                  <base-input label="Fecha Hasta">
                    <input
                      type="date"
                      class="form-control"
                      v-model="filters.date_to"
                      @change="loadOrders"
                    />
                  </base-input>
                </div>
                <div class="col-md-6 d-flex align-items-end">
                  <base-button
                    type="primary"
                    @click="setToday"
                    class="mr-2"
                  >
                    Hoy
                  </base-button>
                  <base-button
                    type="info"
                    @click="setTomorrow"
                    class="mr-2"
                  >
                    Mañana
                  </base-button>
                </div>
              </div>

              <!-- Alerta de hora límite -->
              <div class="alert mt-3" v-if="showCutoffAlert" 
                   :class="nextPendingOrderDate ? 'alert-warning' : 'alert-info'">
                <i class="ni ni-time-alarm"></i>
                <strong>Hora límite de aprobación:</strong> <strong>{{ cutoffTime }}</strong>
                <div class="mt-2">
                  <strong>Tiempo restante:</strong> {{ timeRemaining }}
                </div>
                <div class="mt-1" v-if="nextPendingOrderDate">
                  <small class="text-white">
                    Los pedidos no aprobados antes de esta hora se aprobarán automáticamente.
                  </small>
                </div>
              </div>
            </div>

            <!-- Tabla -->
            <div class="table-responsive" v-if="!loading">
              <table class="table align-items-center table-flush">
                <thead class="thead-light">
                  <tr>
                    <th>ID</th>
                    <th>Fecha</th>
                    <th>Estado</th>
                    <th class="text-center">Items</th>
                    <th class="text-center">Total Solicitado</th>
                    <th class="text-center">Total Sugerido</th>
                    <th class="text-center">Total Aprobado</th>
                    <th>Creado por</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="order in filteredOrders" :key="order.id">
                    <td>
                      <strong>#{{ order.id }}</strong>
                    </td>
                    <td>{{ formatDate(order.date) }}</td>
                    <td>
                      <badge :type="getStatusColor(order.status_id)">
                        {{ getStatusDisplayName(order) }}
                      </badge>
                    </td>
                    <td class="text-center">
                      <badge type="default">{{ order.total_items }} platos</badge>
                    </td>
                    <td class="text-center">
                      <badge type="primary">{{ order.total_solicitado }}</badge>
                    </td>
                    <td class="text-center">
                      <badge type="warning">{{ order.total_sugerido }}</badge>
                    </td>
                    <td class="text-center">
                      <badge type="success">{{ order.total_aprobado || '-' }}</badge>
                    </td>
                    <td>
                      <small>{{ order.creator_name || '-' }}</small>
                      <br>
                      <small class="text-muted">{{ formatDateTime(order.created_at) }}</small>
                    </td>
                    <td>
                      <base-button
                        type="primary"
                        size="sm"
                        @click="viewOrder(order.id)"
                      >
                        <i class="ni ni-zoom-split-in"></i> Ver Detalle
                      </base-button>
                    </td>
                  </tr>
                  <tr v-if="filteredOrders.length === 0">
                    <td colspan="9" class="text-center">
                      No hay pedidos para esta fecha
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="card-body" v-else>
              <div class="text-center">
                <i class="fas fa-spinner fa-spin fa-3x"></i>
                <p class="mt-3">Cargando pedidos...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import backofficeApi from '@/gateways/backoffice-api';
import { toV3 } from '@/utils/apiHelpers';

export default {
  name: 'ListPedidosTienda',
  data() {
    return {
      loading: false,
      orders: [],
      filters: {
        date_from: null,
        date_to: null
      },
      dateRange: {
        min_date: null,
        max_date: null
      },
      cutoffTime: null,
      timeRemaining: '',
      intervalId: null
    };
  },
  computed: {
    filteredOrders() {
      // Filtrar pedidos en estado borrador (status_id === 4)
      return this.orders.filter(order => order.status_id !== 4);
    },
    showCutoffAlert() {
      // Siempre mostrar la alerta si hay cutoff_time configurado
      return !!this.cutoffTime;
    },
    nextPendingOrderDate() {
      // Obtener la fecha del próximo pedido pendiente de aprobar (status_id = 5)
      const today = new Date().toISOString().split('T')[0];
      
      const pendingOrders = this.orders
        .filter(order => order.status_id === 5 && order.date)
        .map(order => order.date.split('T')[0])
        .filter(date => date >= today)
        .sort();
      
      return pendingOrders.length > 0 ? pendingOrders[0] : null;
    }
  },
  async created() {
    // Primero cargar sin filtros para obtener el rango de fechas
    await this.loadInitialDateRange();
    // Luego cargar los pedidos con el rango establecido
    await this.loadOrders();
    // loadCutoffTime() ahora se llama desde loadOrders()
    this.startTimer();
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },
  methods: {
    async loadInitialDateRange() {
      try {
        // Obtener catering_id del usuario autenticado
        const user = this.$store.state.authBackoffice.user;
        const cateringId = user ? user.catering_id : undefined;
        
        // Cargar sin filtros para obtener el rango de fechas
        const response = await backofficeApi.get(toV3('physical-orders'), {
          params: {
            catering_id: cateringId,
            per_page: 1 // Solo necesitamos el date_range, no los pedidos
          }
        });

        if (response.data.success && response.data.detail.date_range) {
          const dateRange = response.data.detail.date_range;
          if (dateRange.min_date && dateRange.max_date) {
            this.dateRange = dateRange;
            this.filters.date_from = dateRange.min_date;
            this.filters.date_to = dateRange.max_date;
          }
        }
      } catch (error) {
        console.error('Error loading date range:', error);
      }
    },
    async loadOrders() {
      this.loading = true;
      try {
        // Obtener catering_id del usuario autenticado
        const user = this.$store.state.authBackoffice.user;
        const cateringId = user ? user.catering_id : undefined;
        
        // Construir parámetros de la petición
        const params = {
          catering_id: cateringId
        };
        
        // Si hay rango de fechas, usar date_from y date_to, sino dejar que el backend use el default
        if (this.filters.date_from && this.filters.date_to) {
          params.date_from = this.filters.date_from;
          params.date_to = this.filters.date_to;
        }
        
        // Petición al backoffice en /api/v3/physical-orders
        const response = await backofficeApi.get(toV3('physical-orders'), {
          params
        });

        if (response.data.success) {
          this.orders = response.data.detail.orders || [];
          
          // Actualizar el rango de fechas si viene en la respuesta
          if (response.data.detail.date_range) {
            this.dateRange = response.data.detail.date_range;
          }
          
          // Actualizar cutoff_time si viene en la respuesta
          if (response.data.detail.cutoff_time) {
            this.cutoffTime = response.data.detail.cutoff_time;
          }
          
          // Cargar cutoff_time de los pedidos si no vino en la respuesta general
          this.loadCutoffTime();
        }
      } catch (error) {
        console.error('Error loading orders:', error);
        this.$notify({
          type: 'danger',
          message: 'Error al cargar los pedidos'
        });
      } finally {
        this.loading = false;
      }
    },
    async loadCutoffTime() {
      try {
        // Buscar cutoff_time en cualquier pedido que lo tenga (como fallback)
        if (!this.cutoffTime) {
          const orderWithCutoff = this.orders.find(order => order.cutoff_time);
          if (orderWithCutoff) {
            this.cutoffTime = orderWithCutoff.cutoff_time;
          }
        }
      } catch (error) {
        console.error('Error loading cutoff time:', error);
      }
    },
    startTimer() {
      this.updateTimeRemaining();
      this.intervalId = setInterval(() => {
        this.updateTimeRemaining();
      }, 60000); // Actualizar cada minuto
    },
    updateTimeRemaining() {
      if (!this.cutoffTime) return;

      // Verificar si hay pedidos pendientes de aprobar
      const nextPendingDate = this.nextPendingOrderDate;
      
      if (!nextPendingDate) {
        // No hay pedidos pendientes
        this.timeRemaining = 'No hay pedidos pendientes';
        return;
      }

      const now = new Date();
      const [hours, minutes] = this.cutoffTime.split(':');
      
      // Usar formato YYYY-MM-DD para evitar problemas de zona horaria
      // Crear fecha del pedido en formato local (sin hora para evitar conversión UTC)
      const [year, month, day] = nextPendingDate.split('-');
      const orderDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      // Calcular la fecha límite: es el mismo día del pedido a la hora configurada
      const cutoffDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      cutoffDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      const diff = cutoffDate - now;

      if (diff > 0) {
        const totalHours = Math.floor(diff / (1000 * 60 * 60));
        const minutesLeft = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        // Si es más de 24 horas, mostrar días + horas
        if (totalHours >= 24) {
          const daysLeft = Math.floor(totalHours / 24);
          const hoursLeft = totalHours % 24;
          const orderDateStr = orderDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
          this.timeRemaining = `${daysLeft}d ${hoursLeft}h ${minutesLeft}m (pedido del ${orderDateStr})`;
        } else {
          const orderDateStr = orderDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
          this.timeRemaining = `${totalHours}h ${minutesLeft}m (pedido del ${orderDateStr})`;
        }
      } else {
        // Ya pasó la hora límite para este pedido
        const orderDateStr = orderDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
        this.timeRemaining = `Tiempo agotado (pedido del ${orderDateStr})`;
      }
    },
    setToday() {
      const today = new Date().toISOString().split('T')[0];
      this.filters.date_from = today;
      this.filters.date_to = today;
      this.loadOrders();
    },
    setTomorrow() {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      this.filters.date_from = tomorrowStr;
      this.filters.date_to = tomorrowStr;
      this.loadOrders();
    },
    applyDateRange() {
      // Aplicar el rango de fechas automáticamente calculado
      if (this.dateRange.min_date && this.dateRange.max_date) {
        this.filters.date_from = this.dateRange.min_date;
        this.filters.date_to = this.dateRange.max_date;
        this.loadOrders();
      }
    },
    viewOrder(id) {
      this.$router.push({ name: 'pedidos-tienda-detalle', params: { id } });
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('es-ES');
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      return new Date(dateTime).toLocaleString('es-ES');
    },
    getStatusColor(statusId) {
      const colors = {
        1: 'success', // Aprobado
        2: 'warning', // Ajustado
        3: 'danger',  // Rechazado
        4: 'default', // Borrador
        5: 'warning'  // Enviado (Por revisar) - amarillo
      };
      return colors[statusId] || 'default';
    },
    getStatusDisplayName(order) {
      // Si está en estado enviado (status_id === 5), mostrar "Por revisar"
      if (order.status_id === 5) {
        return 'Por revisar';
      }
      return order.status_name;
    }
  }
};
</script>

